<template>
    <b-card no-body header-class="d-flex justify-content-between align-items-start" v-if="!hideWhenEmpty || ((courtAppearances != null) && (courtAppearances.length > 0))">
        <template v-slot:header>
            <h5>Representative/s</h5>
            <b-button size="sm" variant="outline-secondary" v-on:click="addCourtAppearance" v-if="!disabled"><i class="fas fa-plus mr-1"></i>Add</b-button>
        </template>
        <b-list-group flush>
            <b-list-group-item v-if="(courtAppearances == null) || (courtAppearances.length == 0)">
                The list is currently empty
            </b-list-group-item>
            <b-list-group-item class="d-flex justify-content-between align-items-start" v-for="courtAppearance in courtAppearances" :key="courtAppearance.courtAppearanceId">
                <div class="p-0 m-0 flex-grow-1">
                    <div class="font-weight-bold">{{ courtAppearance.counselName }}</div>
                    <div>appearing for</div>
                    <div class="font-weight-bold">{{ courtAppearance.partyName }}</div>
                </div>
                <b-button-group size="sm" v-if="!disabled">
                    <b-button variant="outline-secondary" title="Edit the representative" v-on:click="editCourtAppearance(courtAppearance)"><i class="fas fa-edit mr-1"></i>Edit</b-button>
                    <b-button variant="outline-danger" title="Remove the representative from the order" v-on:click="removeCourtAppearance(courtAppearance)"><i class="fas fa-times mr-1"></i>Remove</b-button>
                </b-button-group>
            </b-list-group-item>
        </b-list-group>
        <validation-observer v-slot="{ passes }" slim>
            <b-modal size="lg" centered :title="(currentCourtAppearance.courtAppearanceId == null) ? 'New Representative' : 'Edit Representative'" v-model="editCourtAppearanceDialogShown" v-on:ok.prevent="passes(createOrUpdateCourtAppearance)">
                <b-alert v-model="showErrors" variant="danger" dismissible>
                    <div v-for="(error, index) in currentErrors" :key="index">
                        <p>{{ error }}</p>
                    </div>
                </b-alert>
                <b-form-row>
                    <b-form-group label="Title"
                                  label-for="counselTitle"
                                  class="col-md-2">
                        <b-form-input name="counselTitle"
                                      autofocus
                                      v-model="currentCourtAppearance.counselTitle">
                        </b-form-input>
                    </b-form-group>
                    <validation-provider rules="required" name="Last Name" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Last Name"
                                      label-for="counselLastName"
                                      class="col-md-5">
                            <b-form-input name="counselLastName"
                                          v-model="currentCourtAppearance.counselLastName"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <b-form-group label="Given Names"
                                  label-for="counselGivenNames"
                                  class="col-md-5">
                        <b-form-input name="counselGivenNames"
                                      v-model="currentCourtAppearance.counselGivenNames">
                        </b-form-input>
                    </b-form-group>
                    <validation-provider rules="required" name="Party Name" v-slot="{ valid, errors }" slim>
                        <b-form-group label="Party Last Name"
                                      label-for="partyLastName"
                                      description="or Organisation/Company Name"
                                      class="col-md-6">
                            <b-form-input name="partyName"
                                          v-model="currentCourtAppearance.partyLastName"
                                          :state="errors[0] ? false : null">
                            </b-form-input>
                            <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                        </b-form-group>
                    </validation-provider>
                    <b-form-group label="Party Given Names"
                                  label-for="partyGivenNames"
                                  description="leave empty for Organisation/Company"
                                  class="col-md-6">
                        <b-form-input name="partyGivenNames"
                                      v-model="currentCourtAppearance.partyGivenNames">
                        </b-form-input>
                    </b-form-group>
                </b-form-row>
            </b-modal>
        </validation-observer>
    </b-card>
</template>

<script>
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import axios from 'axios';

    import { ValidationProvider, ValidationObserver } from 'vee-validate';
    import { extend, localize } from 'vee-validate';
    import { required } from 'vee-validate/dist/rules';
    import en from 'vee-validate/dist/locale/en.json';
    extend('required', required);
    localize('en', en);
    
    import ErrorsMixin from '../mixins/ErrorsMixin';
    
    Vue.use(BootstrapVue);
    Vue.component('validation-provider', ValidationProvider);
    Vue.component('validation-observer', ValidationObserver);
    
    export default {
        name: 'CourtAppearances',
        mixins: [ErrorsMixin],
        props: {
            apiControllerUrl: {
                type: String,
                required: true
            },
            courtMatterId: {
                type: String,
                required: true
            },
            disabled: {
                type: Boolean,
                default: false
            },
            hideWhenEmpty: {
                type: Boolean,
                default: false
            }
        },
        data: function () {
            return {
                courtAppearances: null,
                currentCourtAppearance: { courtAppearanceId: null, counselTitle: null, counselLastName: null, counselGivenNames: null, partyLastName: null, partyGivenNames: null },
                currentErrors: [],
                editCourtAppearanceDialogShown: false
            };
        },
        watch: {
            courtMatterId: function () {
                this.reload();
            }
        },
        computed: {
            showErrors: function () {
                return this.currentErrors.length > 0;
            }
        },
        methods: {
            reload: function () {
                
                this.currentErrors = [];
                if (this.courtMatterId) {
                    return axios
                        .get(this.apiControllerUrl + '/list/' + this.courtMatterId)
                        .then(response => {
                            this.courtAppearances = response.data;
                        })
                        .catch(ex => {
                            this.currentErrors = this.handleAxiosException(ex);
                        })
                        .finally(() => {
                            if (this.currentErrors.length != 0) {
                                this.showErrorsToast(this.currentErrors);
                            }
                        });
                }
                return Promise.resolve();
            },
            addCourtAppearance: function () {
                this.currentCourtAppearance = { courtAppearanceId: null, counselTitle: null, counselLastName: null, counselGivenNames: null, partyLastName: null, partyGivenNames: null };
                this.currentErrors = [];
                this.editCourtAppearanceDialogShown = true;
            },
            editCourtAppearance: function (courtAppearance) {
                this.currentCourtAppearance = JSON.parse(JSON.stringify(courtAppearance));
                this.currentErrors = [];
                this.editCourtAppearanceDialogShown = true;
            },
            removeCourtAppearance: function (courtAppearance) {
                this.currentErrors = [];
                axios
                    .delete(this.apiControllerUrl + '/' + courtAppearance.courtAppearanceId)
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length != 0) {
                            this.showErrorsToast(this.currentErrors);
                        }
                    });
            },
            createOrUpdateCourtAppearance: function () {
                var data = {
                    courtMatterId: this.courtMatterId,
                    ...this.currentCourtAppearance
                };
                if (data.courtAppearanceId == null) {
                    delete data.courtAppearanceId;
                }
                this.currentErrors = [];
                axios
                    .request(
                        {
                            method: (this.currentCourtAppearance.courtAppearanceId == null) ? 'post' : 'put',
                            url: this.apiControllerUrl,
                            data: JSON.stringify(data),
                            headers: {
                                'Content-Type': 'application/json'
                            }
                        })
                    .then(() => {
                        this.reload();
                    })
                    .catch(ex => {
                        this.currentErrors = this.handleAxiosException(ex);
                    })
                    .finally(() => {
                        if (this.currentErrors.length == 0) {
                            this.editCourtAppearanceDialogShown = false;
                        }
                    })
            }
        },
        mounted: function () {
            this.reload();
        }
    }
</script>